import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa6';
import SafeIcon from '../common/SafeIcon';
import { backgroundOptions } from '../common/backgrounds';

const { 
  FiUser, FiGlobe, FiMail, FiMapPin, FiExternalLink, FiFolder, FiZap
} = FiIcons;

const SocialIcons = {
  linkedin: FaIcons.FaLinkedin,
  twitter: FaIcons.FaXTwitter,
  github: FaIcons.FaGithub,
  instagram: FaIcons.FaInstagram,
  youtube: FaIcons.FaYoutube,
  tiktok: FaIcons.FaTiktok,
  facebook: FaIcons.FaFacebook,
  dribbble: FaIcons.FaDribbble
};

const presetIcons = {
  User: FiIcons.FiUser,
  Code: FiIcons.FiCode,
  Monitor: FiIcons.FiMonitor,
  Database: FiIcons.FiDatabase,
  Layers: FiIcons.FiLayers,
  Tool: FiIcons.FiTool,
  Zap: FiIcons.FiZap,
  Brain: FiIcons.FiBrain
};

function PreviewCard({ bioData }) {
  const containerRef = useRef(null);
  const isEmpty = (str) => !str || str.trim().length === 0;
  const currentTime = new Date().toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit' });

  useEffect(() => {
    const cards = containerRef.current?.querySelectorAll('.chroma-card');
    const handleMouseMove = (e) => {
      const card = e.currentTarget;
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      card.style.setProperty('--mouse-x', `${x}px`);
      card.style.setProperty('--mouse-y', `${y}px`);
    };
    cards?.forEach(card => card.addEventListener('mousemove', handleMouseMove));
    return () => cards?.forEach(card => card.removeEventListener('mousemove', handleMouseMove));
  }, [bioData]);

  let bgCss = '';
  let bgWrapper = '';
  
  if (bioData.backgroundType === 'solid') {
      bgCss = `background: ${bioData.customBackground || '#000'};`;
  } else if (bioData.backgroundType === 'gradient') {
      bgCss = `background: ${bioData.customBackground || 'linear-gradient(to bottom, #000, #111)'};`;
  } else {
      const currentBg = backgroundOptions.find(b => b.id === bioData.backgroundId) || backgroundOptions[0];
      bgCss = currentBg.css;
      bgWrapper = currentBg.wrapper;
  }

  return (
    <div 
      className="w-full flex justify-center relative min-h-[600px] rounded-3xl overflow-hidden border border-zinc-800"
      style={{
        transform: 'translate3d(0,0,0)',
        '--card-bg': bioData.colors.card,
        '--text-main': bioData.colors.text,
        '--accent': bioData.colors.accent,
        '--spotlight': 'rgba(255,255,255,0.08)'
      }}
    >
      <div className="absolute inset-0 z-0">
         <style>{`.preview-css { ${bgCss} width: 100%; height: 100%; }`}</style>
         <div className="preview-css"></div>
      </div>
      <div className="absolute inset-0 z-0" style={{ isolation: 'isolate', pointerEvents: 'none' }}>
         {bgWrapper && <div dangerouslySetInnerHTML={{ __html: bgWrapper }} />}
      </div>

      <style>{`
        .chroma-card {
            position: relative;
            background: var(--card-bg);
            border: 1px solid rgba(255,255,255,0.08);
            border-radius: 24px;
            overflow: hidden;
            transition: transform 0.2s ease, border-color 0.3s ease;
            --mouse-x: 50%; --mouse-y: 50%;
            color: var(--text-main);
            backdrop-filter: blur(12px);
            -webkit-backdrop-filter: blur(12px);
            box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
        }
        .chroma-card::before {
            content: ''; position: absolute; inset: 0;
            background: radial-gradient(800px circle at var(--mouse-x) var(--mouse-y), var(--spotlight), transparent 40%);
            opacity: 0; transition: opacity 0.5s ease; pointer-events: none; z-index: 2;
        }
        .chroma-card:hover::before { opacity: 1; }
        .chroma-card:hover { border-color: var(--accent); box-shadow: 0 10px 40px -10px rgba(0,0,0,0.5); }
        .chroma-content { position: relative; z-index: 10; height: 100%; }
        .accent-text { color: var(--accent); }
      `}</style>
      
      <div 
        ref={containerRef}
        className="w-full max-w-6xl p-4 sm:p-6 lg:p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5 auto-rows-fr relative z-10"
      >
        {/* ROW 1: Profile & Time */}
        
        {/* Profile Card - Span 3 on LG (60%) */}
        <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} className="chroma-card md:col-span-2 lg:col-span-3 p-6 sm:p-8">
          <div className="chroma-content flex flex-col-reverse sm:flex-row items-center sm:items-start justify-between gap-6">
            <div className="flex-1 flex flex-col w-full text-center sm:text-left min-w-0">
                <div className="text-[10px] font-mono mb-3 px-2 py-1 bg-white/10 rounded w-fit mx-auto sm:mx-0 uppercase tracking-widest opacity-60">Welcome</div>
                <h1 className="text-3xl sm:text-5xl font-bold mb-2 tracking-tight leading-tight break-words">{isEmpty(bioData.name) ? 'Your Name' : bioData.name}</h1>
                <p className="text-lg sm:text-xl font-light mb-6 opacity-80 break-words">{isEmpty(bioData.title) ? 'Creative Creator' : bioData.title}</p>
                
                <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-auto">
                    {Object.keys(bioData.socials).map(key => {
                        const url = bioData.socials[key];
                        const IconComp = SocialIcons[key];
                        if (!url || !IconComp) return null;
                        return (
                            <a key={key} href={url} target="_blank" rel="noreferrer" className="p-3 bg-white/5 hover:bg-white/20 rounded-xl border border-white/10 transition-colors group flex items-center justify-center" title={key}>
                                <SafeIcon icon={IconComp} className="text-lg group-hover:text-[var(--accent)] transition-colors" />
                            </a>
                        );
                    })}
                </div>
            </div>
            <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full border-4 border-white/5 overflow-hidden bg-white/5 flex items-center justify-center shrink-0 shadow-2xl">
                {bioData.avatar ? (
                  <img src={bioData.avatar} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <SafeIcon icon={presetIcons[bioData.presetIcon] || FiUser} className="text-5xl opacity-50" />
                )}
            </div>
          </div>
        </motion.div>

        {/* Time Card - Span 2 on LG (40%) */}
        <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} transition={{delay:0.1}} className="chroma-card lg:col-span-2 p-6 flex flex-col justify-center items-center text-center">
            <div className="chroma-content flex flex-col items-center justify-center h-full">
                <div className="text-3xl sm:text-4xl font-mono font-bold mb-1 tracking-wider">{currentTime}</div>
                <div className="flex items-center gap-2 justify-center opacity-60 text-[10px] uppercase tracking-widest">
                    <SafeIcon icon={FiGlobe} /> {isEmpty(bioData.timezone) ? 'UTC' : bioData.timezone}
                </div>
            </div>
        </motion.div>

        {/* ROW 2: About & Selected Work */}

        {/* About Card - Span 2 on LG (40%) - INCREASED */}
        <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} transition={{delay:0.2}} className="chroma-card lg:col-span-2 p-6 sm:p-8 flex flex-col">
            <div className="chroma-content flex flex-col h-full">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mb-4 accent-text shrink-0">
                    <SafeIcon icon={FiUser} />
                </div>
                <h3 className="text-lg font-bold mb-3">About</h3>
                <p className="text-sm leading-relaxed whitespace-pre-wrap opacity-70 flex-1">{isEmpty(bioData.about) ? 'Tell us about yourself...' : bioData.about}</p>
            </div>
        </motion.div>

        {/* Selected Work Card - Span 3 on LG (60%) - REDUCED */}
        <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} transition={{delay:0.3}} className="chroma-card md:col-span-2 lg:col-span-3 p-6 sm:p-8">
             <div className="chroma-content flex flex-col h-full">
                <div className="flex items-center gap-4 mb-6">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center accent-text shrink-0">
                        <SafeIcon icon={FiFolder} className="text-xl" />
                    </div>
                    <div className="flex-1 flex items-center justify-between flex-wrap gap-2">
                         <h3 className="text-xl font-bold">Selected Work</h3>
                         <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 rounded-full border border-white/10 text-xs font-mono whitespace-nowrap">
                            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                            {isEmpty(bioData.availability) ? 'Available' : bioData.availability}
                        </div>
                    </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1">
                    {bioData.projects && bioData.projects.length > 0 ? (
                        bioData.projects.map((proj, i) => (
                            <a key={i} href={proj.url || '#'} target="_blank" rel="noreferrer" className="group p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/20 transition-all flex items-start gap-3 h-full">
                                <div className="mt-1 opacity-50"><SafeIcon icon={FiExternalLink} className="text-sm" /></div>
                                <div className="min-w-0">
                                    <div className="font-bold text-sm group-hover:text-[var(--accent)] transition-colors truncate">{proj.name || 'Untitled Project'}</div>
                                    <div className="text-xs opacity-60 line-clamp-2">{proj.desc || 'No description'}</div>
                                </div>
                            </a>
                        ))
                    ) : (
                        <div className="col-span-1 sm:col-span-2 flex items-center justify-center py-8 opacity-40 italic h-full bg-white/5 rounded-xl border border-dashed border-white/10">
                            Add projects to showcase your work...
                        </div>
                    )}
                </div>
            </div>
        </motion.div>

        {/* ROW 3: Contact & Tech Stack */}

        {/* Contact Card - Span 2 on LG (40%) - INCREASED */}
         <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} transition={{delay:0.4}} className="chroma-card md:col-span-2 lg:col-span-2 p-6 sm:p-8">
            <div className="chroma-content flex flex-col justify-between h-full">
                <h3 className="text-lg font-bold mb-4">Contact</h3>
                <div className="space-y-3 flex-1">
                    <div className="p-4 bg-white/5 rounded-xl border border-white/5 hover:bg-white/10 transition-colors">
                        <div className="flex items-center gap-2 text-xs opacity-50 mb-1"><SafeIcon icon={FiMail} /> Email</div>
                        <div className="text-sm break-all">{isEmpty(bioData.email) ? 'email@example.com' : bioData.email}</div>
                    </div>
                    <div className="p-4 bg-white/5 rounded-xl border border-white/5 hover:bg-white/10 transition-colors">
                        <div className="flex items-center gap-2 text-xs opacity-50 mb-1"><SafeIcon icon={FiMapPin} /> Location</div>
                        <div className="text-sm break-words">{isEmpty(bioData.location) ? 'Worldwide' : bioData.location}</div>
                    </div>
                </div>
            </div>
        </motion.div>

        {/* Tech Stack Card - Span 3 on LG (60%) - REDUCED */}
        <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} transition={{delay:0.5}} className="chroma-card md:col-span-2 lg:col-span-3 p-6 sm:p-8">
            <div className="chroma-content flex flex-col justify-center h-full">
                <div className="flex items-center gap-3 mb-6">
                    <SafeIcon icon={FiZap} className="accent-text text-2xl" />
                    <h3 className="text-lg font-bold">Tech Stack</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                    {bioData.learning.length > 0 ? bioData.learning.map((skill, i) => (
                        <span key={i} className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-sm hover:bg-white/10 transition-colors cursor-default whitespace-nowrap">{skill}</span>
                    )) : <span className="text-sm opacity-50 italic">Add skills...</span>}
                </div>
            </div>
        </motion.div>
      </div>
    </div>
  );
}

export default PreviewCard;